import { create } from 'zustand';
import type { RegistrationFormData, LoadingState } from '@/types';

interface RegistrationState {
  // Form data
  formData: Partial<RegistrationFormData>;
  updateFormData: (data: Partial<RegistrationFormData>) => void;
  clearFormData: () => void;

  // Registration flow state
  currentStep: 'form' | 'photo' | 'review' | 'processing' | 'complete';
  setStep: (step: RegistrationState['currentStep']) => void;

  // Loading state
  loadingState: LoadingState;
  setLoadingState: (state: LoadingState) => void;

  // Error handling
  error: string | null;
  setError: (error: string | null) => void;

  // Capacity info
  remainingCapacity: number;
  capacityPercentage: number;
  updateCapacity: (remaining: number, percentage: number) => void;

  // Registration availability
  isRegistrationOpen: boolean;
  setRegistrationOpen: (isOpen: boolean) => void;

  // Reset entire state
  reset: () => void;
}

const initialFormData: Partial<RegistrationFormData> = {
  fullName: '',
  email: '',
  age: undefined,
  contactNumber: '',
  attendanceDays: [],
  isFeastMagnolia: true,
  howDidYouLearn: 'social_media',
  bringingKids: false,
};

export const useRegistrationStore = create<RegistrationState>((set) => ({
  // Form data
  formData: initialFormData,
  updateFormData: (data) =>
    set((state) => ({
      formData: { ...state.formData, ...data },
    })),
  clearFormData: () => set({ formData: initialFormData }),

  // Registration flow
  currentStep: 'form',
  setStep: (step) => set({ currentStep: step }),

  // Loading state
  loadingState: 'idle',
  setLoadingState: (state) => set({ loadingState: state }),

  // Error handling
  error: null,
  setError: (error) => set({ error }),

  // Capacity info
  remainingCapacity: 1000,
  capacityPercentage: 0,
  updateCapacity: (remaining, percentage) =>
    set({
      remainingCapacity: remaining,
      capacityPercentage: percentage,
    }),

  // Registration availability
  isRegistrationOpen: true,
  setRegistrationOpen: (isOpen) => set({ isRegistrationOpen: isOpen }),

  // Reset
  reset: () =>
    set({
      formData: initialFormData,
      currentStep: 'form',
      loadingState: 'idle',
      error: null,
    }),
}));